import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Database, FileUp, Play, RefreshCw, Settings2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, getApiErrorMessage } from "@/lib/api";

const emptyConfig = {
  acquisition_mode: "MANUAL_ARTIFACT",
  authorization_reference: "",
  regular_only_certified: false,
  credential_env_var: "",
  endpoint_hostname: "",
  schedule: "",
  sync_enabled: false,
  sync_interval_hours: 24,
  notes: "",
};

const tone = (state) => {
  if (["ACTIVE", "SUCCEEDED", "PASSED", "PUBLISHED", "IMPLEMENTED"].includes(state)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (["FAILED", "POLICY_BLOCKED", "CRITICAL"].includes(state)) return "bg-red-50 text-red-700 border-red-200";
  if (["RUNNING", "QUEUED"].includes(state)) return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-amber-50 text-amber-800 border-amber-200";
};

const Pill = ({ children, state = children }) => (
  <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${tone(state)}`}>{children}</span>
);

const when = (value) => value ? new Date(value).toLocaleString() : "Never";

export default function ResearchOperations() {
  const [overview, setOverview] = useState({});
  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [methods, setMethods] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [configSource, setConfigSource] = useState(null);
  const [config, setConfig] = useState(emptyConfig);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setBusy(true);
    try {
      const [summary, sourceRows, jobRows, qualityRows, methodRows, artifactRows] = await Promise.all([
        adminApi.get("/admin/research-ops/overview"),
        adminApi.get("/admin/research-ops/sources"),
        adminApi.get("/admin/research-ops/jobs"),
        adminApi.get("/admin/research-ops/quality"),
        adminApi.get("/admin/research-ops/methodologies"),
        adminApi.get("/admin/research-ops/artifacts"),
      ]);
      setOverview(summary.data);
      setSources(sourceRows.data.sources || []);
      setJobs(jobRows.data.jobs || []);
      setIssues(qualityRows.data.issues || []);
      setMethods(methodRows.data.methodologies || []);
      setArtifacts(artifactRows.data.artifacts || []);
    } catch (error) {
      if (!quiet) toast.error(getApiErrorMessage(error));
    } finally {
      if (!quiet) setBusy(false);
    }
  }, []);

  const hasActiveJob = useMemo(() => jobs.some((job) => ["QUEUED", "RUNNING"].includes(job.state)), [jobs]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const timer = setInterval(() => load(true), 5000);
    return () => clearInterval(timer);
  }, [hasActiveJob, load]);

  const openConfig = (source) => {
    const stored = source.configuration || {};
    setConfig({ ...emptyConfig, ...stored });
    setConfigSource(source);
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/research-ops/sources/${configSource.id}`, config);
      toast.success(`${configSource.name} entitlement and acquisition policy saved`);
      setConfigSource(null);
      await load(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (source, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const body = new FormData();
    body.append("source_id", source.id);
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/research-ops/artifacts", body);
      toast.success(`${data.artifact.records} real source rows ingested; SHA-256 ${data.artifact.sha256.slice(0, 12)}… retained`);
      await load(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const runResearch = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/research/run");
      toast.success(`Research job ${data.job.id} queued against candidate snapshot ${data.job.snapshot_id}`);
      await load(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const syncSource = async (source) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/research-ops/sources/${source.id}/sync`);
      toast.success(`NAV-history sync job ${data.job.id} queued for allow-listed Regular scheme codes`);
      await load(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Research Operations</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--ink-muted)]">Source entitlement, Regular-only acquisition, lineage, quality gates and immutable publication. Every number below comes from current system state.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load()} disabled={busy}><RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Refresh</Button>
          <Button onClick={runResearch} disabled={busy || hasActiveJob}><Play className="mr-2 h-4 w-4" />{hasActiveJob ? "Job running" : "Build candidate"}</Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div className="flex items-start gap-2"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /><div><strong>Regular-only acquisition is enforced before retention.</strong> AMFI NAVAll is permanently blocked. BSE scheme-master requests are server-filtered to Regular and a mixed response is rejected in full.</div></div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Sources", overview.source_count || 0],
          ["Publishable", overview.publishable_sources || 0],
          ["Running jobs", overview.running_jobs || 0],
          ["Open quality issues", overview.open_quality_issues || 0],
          ["Critical", overview.critical_quality_issues || 0],
        ].map(([label, value]) => <Card key={label}><CardContent className="p-4"><div className="text-xs text-[var(--ink-muted)]">{label}</div><div className="mt-1 font-heading text-2xl text-[var(--brand)]">{value}</div></CardContent></Card>)}
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-[var(--ink)]">Current MF research publication</div>
            {overview.mf_publication ? <div className="mt-1 break-all text-xs text-[var(--ink-muted)]">Snapshot {overview.mf_publication.snapshot_id} · published {when(overview.mf_publication.published_at)} · coverage {Math.round((overview.mf_publication.validation?.coverage || 0) * 100)}%</div> : <div className="mt-1 text-xs text-amber-700">No validated immutable snapshot is published. Existing legacy research, if present, is labelled unverified.</div>}
          </div>
          <Pill state={overview.mf_publication ? "PUBLISHED" : "UNPUBLISHED"}>{overview.mf_publication ? "PUBLISHED" : "UNPUBLISHED"}</Pill>
        </CardContent>
      </Card>

      <Tabs defaultValue="sources">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="sources"><Database className="mr-1.5 h-4 w-4" />Sources</TabsTrigger>
          <TabsTrigger value="jobs"><Activity className="mr-1.5 h-4 w-4" />Jobs</TabsTrigger>
          <TabsTrigger value="quality"><AlertTriangle className="mr-1.5 h-4 w-4" />Quality</TabsTrigger>
          <TabsTrigger value="artifacts"><FileUp className="mr-1.5 h-4 w-4" />Artifacts</TabsTrigger>
          <TabsTrigger value="methods"><CheckCircle2 className="mr-1.5 h-4 w-4" />Methods</TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="space-y-3">
          {sources.map((source) => <Card key={source.id} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><div className="font-semibold text-[var(--ink)]">{source.name}</div><Pill state={source.state}>{source.state.replaceAll("_", " ")}</Pill>{source.publishable && <Pill state="ACTIVE">PUBLISHABLE</Pill>}</div>
                  <div className="mt-1 break-all text-xs text-[var(--ink-muted)]">{source.id} · {source.domain} · plan scope {source.plan_scope}</div>
                  <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
                    <div><span className="text-[var(--ink-muted)]">Records</span><div className="font-medium">{source.records.toLocaleString()}</div></div>
                    <div><span className="text-[var(--ink-muted)]">Latest data</span><div className="font-medium">{source.latest_data_at || "No data"}</div></div>
                    <div><span className="text-[var(--ink-muted)]">Entitlement</span><div className="font-medium">{source.entitlement_verified ? "Verified" : "Missing"}</div></div>
                    <div><span className="text-[var(--ink-muted)]">Plan policy</span><div className="font-medium">{source.regular_only_verified ? "Verified" : "Not verified"}</div></div>
                  </div>
                  {source.dependency && !source.publishable && <div className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">{source.dependency}</div>}
                  {!!source.direct_rows_detected && <div className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">{source.direct_rows_detected} Direct-plan row(s) detected. Publication is blocked.</div>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {!source.policy_blocked && <Button size="sm" variant="outline" onClick={() => openConfig(source)}><Settings2 className="mr-2 h-4 w-4" />Configure</Button>}
                  {source.id === "MF_REGULAR_NAV_HISTORY" && <Button size="sm" variant="outline" onClick={() => syncSource(source)} disabled={busy}><RefreshCw className="mr-2 h-4 w-4" />Sync now</Button>}
                  {source.upload_enabled && <label className={`inline-flex cursor-pointer items-center rounded-md bg-[var(--cta)] px-3 py-2 text-xs font-medium text-white ${busy ? "pointer-events-none opacity-50" : ""}`}><FileUp className="mr-2 h-4 w-4" />Upload artifact<input className="hidden" type="file" onChange={(event) => upload(source, event)} disabled={busy} /></label>}
                </div>
              </div>
            </CardContent>
          </Card>)}
        </TabsContent>

        <TabsContent value="jobs">
          <Card><CardContent className="overflow-x-auto p-0">{!jobs.length ? <div className="p-6 text-sm text-[var(--ink-muted)]">No research job has run.</div> : <table className="w-full min-w-[850px] text-xs"><thead><tr className="border-b text-left text-[var(--ink-muted)]"><th className="p-3">Job</th><th>State</th><th>Progress</th><th>Snapshot</th><th>Created</th><th>Finished</th><th>Result / error</th></tr></thead><tbody className="divide-y">{jobs.map((job) => <tr key={job.id}><td className="p-3 font-medium">{job.id}</td><td><Pill state={job.state}>{job.state}</Pill></td><td>{job.done || 0} / {job.total || 0}</td><td className="max-w-[150px] truncate" title={job.snapshot_id}>{job.snapshot_id}</td><td>{when(job.created_at)}</td><td>{when(job.finished_at)}</td><td className="max-w-[320px] whitespace-normal py-2">{job.error?.message || (job.validation ? `${Math.round(job.validation.coverage * 100)}% coverage · ${job.validation.rated} rated` : "—")}</td></tr>)}</tbody></table>}</CardContent></Card>
        </TabsContent>

        <TabsContent value="quality" className="space-y-3">
          {!issues.length ? <Card><CardContent className="p-6 text-sm text-emerald-700">No open research quality issue.</CardContent></Card> : issues.map((issue) => <Card key={issue.id}><CardContent className="p-4"><div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"><div><div className="font-medium text-[var(--ink)]">{issue.message}</div><div className="mt-1 text-xs text-[var(--ink-muted)]">{issue.id}{issue.source_id ? ` · ${issue.source_id}` : ""}</div>{issue.dependency && <div className="mt-2 text-xs text-amber-800">Required: {issue.dependency}</div>}{issue.evidence && <pre className="mt-2 overflow-auto rounded bg-[var(--surface-2)] p-2 text-[11px]">{JSON.stringify(issue.evidence, null, 2)}</pre>}</div><Pill state={issue.severity}>{issue.severity}</Pill></div></CardContent></Card>)}
        </TabsContent>

        <TabsContent value="artifacts">
          <Card><CardContent className="overflow-x-auto p-0">{!artifacts.length ? <div className="p-6 text-sm text-[var(--ink-muted)]">No authorised research artifact has been retained.</div> : <table className="w-full min-w-[760px] text-xs"><thead><tr className="border-b text-left text-[var(--ink-muted)]"><th className="p-3">File</th><th>Source</th><th>Rows</th><th>SHA-256</th><th>Uploaded</th><th>Status</th></tr></thead><tbody className="divide-y">{artifacts.map((item) => <tr key={item.id}><td className="p-3 font-medium">{item.file_name}</td><td>{item.source_id}</td><td>{item.records}</td><td className="font-mono">{item.sha256?.slice(0, 16)}…</td><td>{when(item.uploaded_at)}</td><td><Pill state={item.status}>{item.status}</Pill></td></tr>)}</tbody></table>}</CardContent></Card>
        </TabsContent>

        <TabsContent value="methods" className="space-y-3">
          {methods.map((method) => <Card key={method.id}><CardHeader><CardTitle className="flex flex-wrap items-center gap-2 text-base">{method.name}<Pill state={method.status}>{method.status}</Pill></CardTitle></CardHeader><CardContent className="text-sm"><div className="text-xs text-[var(--ink-muted)]">{method.id} · version {method.version} · {method.rating_scope}</div><div className="mt-3 font-medium">Real inputs</div><div className="mt-1 flex flex-wrap gap-2">{method.inputs.map((input) => <Pill key={input}>{input}</Pill>)}</div><div className="mt-3 font-medium">Disabled until dependencies exist</div><ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-[var(--ink-muted)]">{method.current_limitations.map((item) => <li key={item}>{item}</li>)}</ul></CardContent></Card>)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!configSource} onOpenChange={(open) => !open && setConfigSource(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>Configure {configSource?.name}</DialogTitle><DialogDescription>Store evidence and secret references only. Passwords, tokens and keys are rejected by the API.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2"><Label>Acquisition mode</Label><select className="w-full rounded-md border bg-white p-2 text-sm" value={config.acquisition_mode} onChange={(e) => setConfig((old) => ({ ...old, acquisition_mode: e.target.value }))}><option>MANUAL_ARTIFACT</option><option>API</option><option>SFTP</option><option>PROVIDER_PORTAL</option></select></div>
            <div className="space-y-2"><Label>Authorisation / contract reference</Label><Input value={config.authorization_reference || ""} onChange={(e) => setConfig((old) => ({ ...old, authorization_reference: e.target.value }))} placeholder="Contract or licence reference" /></div>
            <div className="space-y-2"><Label>Credential environment variable</Label><Input value={config.credential_env_var || ""} onChange={(e) => setConfig((old) => ({ ...old, credential_env_var: e.target.value.toUpperCase() }))} placeholder="RESEARCH_SOURCE_TOKEN" /></div>
            <div className="space-y-2"><Label>Endpoint hostname</Label><Input value={config.endpoint_hostname || ""} onChange={(e) => setConfig((old) => ({ ...old, endpoint_hostname: e.target.value }))} placeholder="provider.example.com" /></div>
            <div className="space-y-2"><Label>Schedule</Label><Input value={config.schedule || ""} onChange={(e) => setConfig((old) => ({ ...old, schedule: e.target.value }))} placeholder="Daily after source close" /></div>
            <label className="flex items-center gap-2 self-end rounded-md border p-2 text-sm"><input type="checkbox" checked={!!config.regular_only_certified} onChange={(e) => setConfig((old) => ({ ...old, regular_only_certified: e.target.checked }))} />Source contract is Regular-only</label>
            {configSource?.id === "MF_REGULAR_NAV_HISTORY" && <>
              <label className="flex items-center gap-2 rounded-md border p-2 text-sm"><input type="checkbox" checked={!!config.sync_enabled} onChange={(e) => setConfig((old) => ({ ...old, sync_enabled: e.target.checked }))} />Enable periodic exact-code sync</label>
              <div className="space-y-2"><Label>Sync interval (hours)</Label><Input type="number" min="1" max="168" value={config.sync_interval_hours || 24} onChange={(e) => setConfig((old) => ({ ...old, sync_interval_hours: Number(e.target.value) }))} /></div>
            </>}
            <div className="space-y-2 sm:col-span-2"><Label>Operational notes</Label><Textarea value={config.notes || ""} onChange={(e) => setConfig((old) => ({ ...old, notes: e.target.value }))} placeholder="Delivery owner, cutoff and escalation details" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConfigSource(null)}>Cancel</Button><Button onClick={saveConfig} disabled={busy}>Save verified source</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
